import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../main';
import { invalidarListasDeCobro } from './invalidaciones';
import { toast } from 'react-toastify';
import { 
    getAllClientsAndSubscriptions, 
    getClientAndSubscriptionById, 
    createClientAndSubscription,
    fetchClientesSinBeneficiario,
    deleteClientAndSubscription,
    restoreClientAndSubscription
} from '../api';
import { Client, Suscripcion } from "../../types/index";
import { ClienteSinBeneficiario } from '../../types/clientes';
import { useUser } from '@clerk/clerk-react';
import { useTenantCode } from './useTenantCode';


interface ClientSubscriptionData {
    client: Partial<Client>;
    subscription: Partial<Suscripcion>;
}

interface DeleteClientSubscriptionParams {
    clientId: string;
    subscriptionId?: string;
    deleteOpenPayments?: boolean;
}

// Query para obtener todos los clientes con sus suscripciones
export const useClientsAndSubscriptionsQuery = (activo?: boolean) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['clients-subscriptions', activo, tenantCode],
        queryFn: () => getAllClientsAndSubscriptions(activo)
    });
};

// Query para obtener un cliente específico con sus suscripciones
export const useClientAndSubscriptionByIdQuery = (id: string) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['client-subscription', id, tenantCode],
        queryFn: () => getClientAndSubscriptionById(id),
        enabled: !!id
    });
};

// Mutación para crear cliente y suscripción
export const useCreateClientAndSubscriptionMutation = () => useMutation({
    mutationFn: (data: ClientSubscriptionData) => createClientAndSubscription(data),
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['clients-subscriptions'] });
        toast.success('Cliente y suscripción creados exitosamente');
    },
    onError: () => {
        toast.error('Error al crear el cliente y la suscripción');
    }
});


// Query para obtener los clientes sin beneficiario
export const useClientsWithoutBeneficiaryQuery = (options?: { enabled?: boolean }) => {
    const tenantCode = useTenantCode();
    return useQuery<ClienteSinBeneficiario[]>({
        queryKey: ['clients-without-beneficiary', tenantCode],
        queryFn: fetchClientesSinBeneficiario,
        ...options
    });
}

/**
 * Hook para gestionar la eliminación lógica de clientes y suscripciones
 * @returns Mutación para eliminar cliente y suscripción
 */
export const useDeleteClientSubscription = () => {
    const { user } = useUser();
    const userName = user?.firstName || user?.username || 'Usuario del sistema';

    return useMutation({
        mutationFn: ({ clientId, subscriptionId, deleteOpenPayments }: DeleteClientSubscriptionParams) => 
            deleteClientAndSubscription(clientId, subscriptionId, userName, deleteOpenPayments),
        
        onSuccess: (data, variables) => {
            // La ficha del cliente se queda abierta tras la baja, así que hay
            // que refrescarla para que pase a mostrarse como inactiva. La clave
            // real lleva el tenantCode al final; react-query casa por prefijo.
            queryClient.invalidateQueries({ queryKey: ['client', variables.clientId] });

            // Listas de cobro (SEPA, Otros pagos…). Antes invalidaba
            // 'client-subscriptions', sin la "s": Otros pagos no se refrescaba.
            invalidarListasDeCobro();
            
            // Si hay eventos actualizados, invalidar también la consulta de eventos
            if (data.eventosActualizados > 0) {
                queryClient.invalidateQueries({ queryKey: ['eventos'] });
            }

            // Si hay pagador actualizado, invalidar la consulta específica
            if (data.updatedPayer) {
                queryClient.invalidateQueries({ 
                    queryKey: ['client', data.updatedPayer.client_id] 
                });
            }

            // Mostrar mensaje de éxito
            toast.success(`Se desactivaron ${data.plazasDesactivadas} plazas y se actualizaron ${data.eventosActualizados} eventos`, 
                { autoClose: 5000 }
            );
        },
        
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || 'Error al dar de baja al cliente';
            toast.error(`Error: ${errorMessage}`, { 
                autoClose: false
            });
            console.error('Error eliminando cliente y suscripción:', error);
        }
    });
};


interface RestoreClientSubscriptionParams {
    clientId: string;
    subscriptionId?: string | null;
}

/**
 * Hook para restaurar cliente y suscripción
 * @returns Mutación para restaurar cliente y suscripción
 */
export const useRestoreClientSubscription = () => {
    return useMutation({
        mutationFn: ({ clientId, subscriptionId }: RestoreClientSubscriptionParams) => 
            restoreClientAndSubscription(clientId, subscriptionId),
        
        onSuccess: (data) => {
            // Invalidar todas las consultas relacionadas
            invalidarListasDeCobro();
            queryClient.invalidateQueries({ queryKey: ['client', 'telefono'] }); // Para limpiar búsquedas por teléfono
            
            toast.success('Cliente restaurado exitosamente');
        },
        
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || 'Error al restaurar el cliente';
            toast.error(`Error: ${errorMessage}`);
            console.error('Error restaurando cliente:', error);
        }
    });
};